import { LoadingDots, SelectableBoxField, TextField } from '@flos/react-ui';
import { useContext, useEffect, useState } from 'react';
import { ConfigContext } from '../../../context/ConfigContext';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../../helpers/templates';
import useService from '../../../hooks/useService';
import useAppState from '../../../state/appState';
import { PetSelector as PetSelectorType, FormConfig, FormValues } from '@topdanmark-onlinesolutions/claim-form-types';
import { Status } from '../../../types/status';
import { AnimalType, ENTITY_MAP, getAnimalType, otherAnimalLabels, mapDataToOptions, titleCase } from './utils';
import { AnimalServiceData, AnimalPolicy } from '../../../types/services';
import { JSX } from 'react/jsx-runtime';
import './styles.scss';

type AnimalValueType = {
    animalType: AnimalType;
    name: string;
    breed: string;
    policyNum: string;
};

const OTHERS = 'OTHERS';

const PetSelector = ({ elementId, petTypeSelectorElementId, petType }: PetSelectorType) => {
    const topConfig = useContext(ConfigContext);
    const { data: animalServiceData, status } = useService<AnimalServiceData>(topConfig.services.animals.pets);

    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const selectedAnimalType: AnimalType = getAnimalType(formValues, petTypeSelectorElementId, petType);

    const value = PetSelector.getValueOrDefault(formValues, formConfig, elementId);

    const [petPolicies, setPetPolicies] = useState<AnimalPolicy[]>([]);
    const [selectBoxValue, setSelectBoxValue] = useState(value.policyNum);
    const [otherAnimal, setOtherAnimal] = useState<{ name: string; breed: string }>(() => {
        const { name, breed } = value;
        return { name, breed };
    });

    const options = mapDataToOptions(petPolicies, selectedAnimalType);

    useEffect(() => {
        if (animalServiceData) {
            const { policies } = animalServiceData;
            setPetPolicies(policies);

            if (!policies.some((policy) => policy.entity === ENTITY_MAP[selectedAnimalType])) {
                setSelectBoxValue(OTHERS);
            }
        }

        if (value.animalType !== selectedAnimalType) {
            setSelectBoxValue(OTHERS);
            setOtherAnimal({
                name: '',
                breed: ''
            });
            setFormValue(elementId, {
                animalType: selectedAnimalType,
                policyNum: OTHERS,
                name: '',
                breed: ''
            });
        }
    }, [animalServiceData, selectedAnimalType, elementId, value.animalType, setFormValue]);

    const selectBoxOptions = () => {
        const boxOptions: { label: string | JSX.Element; value: string; iconShape: string }[] = [];
        options.forEach((option, i) => {
            boxOptions.push({
                label:
                    option.policyNum === OTHERS ? (
                        otherAnimalLabels[selectedAnimalType].other
                    ) : (
                        <>
                            <span className={'boxTitle'}>{option.name}</span>
                            <br />
                            <span className={'boxSubtitle'}>{option.breed}</span>
                        </>
                    ),
                value: option.policyNum,
                iconShape: selectedAnimalType === 'cat' ? 'animal-cat' : selectedAnimalType
            });
        });
        return boxOptions;
    };

    const onSelectBoxChange = (value: string) => {
        if (value === OTHERS) {
            const { name, breed } = otherAnimal;
            setOtherAnimal({
                name: '',
                breed: ''
            });
            setFormValue(elementId, {
                animalType: selectedAnimalType,
                name,
                breed,
                policyNum: OTHERS
            });
        } else {
            const selectedAnimal = options.find((option) => option.policyNum === value);
            if (selectedAnimal) {
                const { name, breed, policyNum } = selectedAnimal;
                setFormValue(elementId, {
                    animalType: selectedAnimalType,
                    name,
                    breed,
                    policyNum
                });
            }
        }
        setSelectBoxValue(value);
    };

    const onTextFieldChange = (key: 'name' | 'breed', value: string) => {
        setOtherAnimal({ ...otherAnimal, [key]: value });
        setFormValue(elementId, {
            ...otherAnimal,
            [key]: value,
            animalType: selectedAnimalType,
            policyNum: OTHERS
        });
    };

    return (
        <div id={elementId} className={'u-spacing-stack-l relative'} data-testid="animal">
            {status === Status.LOADING && (
                <div className="boxPaddingBottom">
                    <LoadingDots centered />
                </div>
            )}
            {status === Status.ERROR && <p className={'textError'}>Der opstod en fejl</p>}
            {status === Status.OK && (
                <>
                    <SelectableBoxField
                        options={selectBoxOptions()}
                        value={value.policyNum}
                        onChangeValue={onSelectBoxChange}
                    />
                    {selectBoxValue === OTHERS && (
                        <>
                            <TextField
                                required
                                id={`${elementId}--name`}
                                label={otherAnimalLabels[selectedAnimalType].nameLabel}
                                value={otherAnimal.name}
                                onChange={({ target: { value } }) => {
                                    onTextFieldChange('name', value);
                                }}
                            />
                            <TextField
                                required
                                id={`${elementId}--breed`}
                                label={otherAnimalLabels[selectedAnimalType].breedLabel}
                                value={otherAnimal.breed}
                                onChange={({ target: { value } }) => {
                                    onTextFieldChange('breed', value);
                                }}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

PetSelector.isValid = (formValues: FormValues, elementId: string) => {
    const valueObj = PetSelector.getValue(formValues, elementId);

    if (!valueObj) {
        return false;
    }

    if (!valueObj.name) {
        return false;
    }

    if (valueObj.policyNum === OTHERS) {
        return !!(valueObj.name && valueObj.breed);
    }

    return !!valueObj.policyNum;
};

PetSelector.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

PetSelector.getValueOrDefault = (formValues: FormValues, formConfig: FormConfig | undefined, elementId: string) => {
    const valueObj: AnimalValueType = PetSelector.getValue(formValues, elementId);

    if (formConfig) {
        const { petTypeSelectorElementId, petType } = getElementFromElementId(formConfig, elementId) as PetSelectorType;
        const selectedAnimalType = getAnimalType(formValues, petTypeSelectorElementId, petType);

        if (valueObj === undefined) {
            return {
                animalType: selectedAnimalType,
                policyNum: '',
                name: '',
                breed: ''
            };
        }
    }

    return valueObj;
};

PetSelector.getValueHuman = (formValues: FormValues, elementId: string) => {
    const value: AnimalValueType = PetSelector.getValue(formValues, elementId);
    const { animalType, breed, name, policyNum } = value;

    return `${titleCase(ENTITY_MAP[animalType])}, ${name}, ${breed}${`, Policenummer: ${
        policyNum === OTHERS ? '' : policyNum
    }`}`;
};

PetSelector.getValueSubmit = (formValues: FormValues, formConfig: FormConfig, elementId: string, caption: string) => {
    const valueObj = PetSelector.getValueOrDefault(formValues, formConfig, elementId);
    const valueStr = PetSelector.getValueHuman(formValues, elementId);
    const componentName = `${elementId}-${valueObj.animalType}`;

    const { animalType, breed, name, policyNum } = valueObj;
    const valueId = {
        animalType,
        name,
        breed,
        policyNum: policyNum === OTHERS ? '' : policyNum
    };
    return getClaimFormSubmitValue('animal', componentName, caption, valueStr, valueId);
};

PetSelector.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as PetSelectorType;
    const { summaryCaption } = element;
    const value: AnimalValueType = PetSelector.getValue(formValues, elementId);

    if (value === undefined) {
        return null;
    }

    const display = (
        <>
            {value.name}
            <br />
            {value.breed}
        </>
    );
    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={display} />;
};

export default PetSelector;
